import React, { useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'

import { TradingControlsQuoteUsd } from './trading-controls-quote-usd'

import type { Pair } from '@root/entities/pair'
import type { TradeType } from '@root/entities/trade-type'
import type { ClassNameProps } from '@root/shared/react'
import type BigNumber from 'bignumber.js'

type QuoteMinimal = { inputAmount: BigNumber; outputAmount: BigNumber }

type InstantTradeQuoteProps = {
  swapDirection: TradeType.SwapDirection
  quoteState: AsyncStateN.AsyncStateN<unknown, QuoteMinimal>
  pair: Pair.PairMinimal
} & ClassNameProps

export const TradingControlsQuote: React.FC<InstantTradeQuoteProps> =
  React.memo(({ pair, quoteState, swapDirection, className }) => {
    const textRef = useRef<HTMLDivElement>(null)
    const [largeTicker, setLargeTicker] = useState(false)

    useEffect(() => {
      const current = textRef.current

      if (current) {
        const largeTicker =
          current.getBoundingClientRect().width >=
          (current.parentElement?.getBoundingClientRect().width ?? 0)

        setLargeTicker(largeTicker)
      }
    }, [quoteState])

    return quoteState.pipe(
      AsyncStateN.match({
        Error: () => <Root />,
        NotInitiated: () => <Root />,
        Loading: () => (
          <Root className={className}>
            <Skeleton width={160} height={13} paddingBlock={1} />
          </Root>
        ),
        Success: (amount) => {
          const amountNative =
            swapDirection === 'buy' ? amount.inputAmount : amount.outputAmount

          const Ticker = largeTicker ? TickerFade : TickerText

          return (
            <Root $swapDirection={swapDirection} className={className}>
              <Text ref={textRef}>
                <Trans
                  i18nKey={`trading.${swapDirection}-quote-for`}
                  components={
                    swapDirection === 'buy'
                      ? [
                          <Numeric
                            key={0}
                            prefix={'~'}
                            value={amount.outputAmount}
                            preset="currency-amount-short"
                          />,
                          <Ticker key={1}>{pair.quote.ticker}</Ticker>,
                          <Numeric
                            key={2}
                            value={amount.inputAmount}
                            preset="currency-amount-short"
                            currency={pair.base.ticker}
                          />,
                        ]
                      : [
                          <Numeric
                            key={0}
                            value={amount.inputAmount}
                            preset="currency-amount-short"
                          />,
                          <Ticker key={1}>{pair.quote.ticker}</Ticker>,
                          <Numeric
                            key={2}
                            prefix={'~'}
                            value={amount.outputAmount}
                            preset="currency-amount-short"
                            currency={pair.base.ticker}
                          />,
                        ]
                  }
                />
                <TradingControlsQuoteUsd amountNative={amountNative} />
              </Text>
            </Root>
          )
        },
      }),
    )
  })

const Root = styled.div<{ $swapDirection?: TradeType.SwapDirection }>`
  color: ${({ $swapDirection }) =>
    $swapDirection === 'buy' ? 'var(--upAccent-9)' : 'var(--downAccent-9)'};

  font-size: 12px;
  font-weight: 500;
  display: grid;
  min-height: 14px;
  place-content: center;
`

const Text = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 2px;
`

const TickerText = styled.span`
  text-transform: uppercase;
`

const TickerFade = styled(TickerText)`
  --horizontal-mask: linear-gradient(
    90deg,
    transparent,
    black 0,
    black calc(100% - 5px),
    transparent
  );

  overflow: hidden;
  mask-image: var(--horizontal-mask);
  mask-size: 100%;
  mask-position: 0 0;
  mask-repeat: no-repeat, no-repeat;
`
